import { OauthAttemptStatus } from "@coeff/api";
import { message } from "antd";
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import styled from "styled-components";

import { CoeLogo, Typography } from "../components";

// Seconds to wait before auto-closing the success page.
const AUTO_CLOSE_SECONDS = 5;

const Layout = styled.div`
  height: 100vh;
  text-align: center;
  padding: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
`;

export const OauthAttemptRedirect: React.FC = () => {
  const { search } = useLocation();

  const searchParams = new URLSearchParams(search);

  const isSuccessStatus = searchParams.get("status") === OauthAttemptStatus.Success;

  const isFromAddon = searchParams.get("from") === "addon";
  const isFromWebapp = searchParams.get("from") === "webapp";

  // Only the addon and webapp flows auto-close the tab.
  const shouldAutoClose = isSuccessStatus && (isFromAddon || isFromWebapp);

  const [secondsLeft, setSecondsLeft] = useState(AUTO_CLOSE_SECONDS);

  useEffect(() => {
    if (isSuccessStatus) {
      message.success("Connection successful");
    }
  }, [isSuccessStatus]);

  // Success flow (addon or webapp): stay on the page and auto-close after a
  // short countdown.
  useEffect(() => {
    if (!shouldAutoClose) {
      return;
    }

    const interval = setInterval(() => {
      setSecondsLeft(prev => {
        if (prev <= 1) {
          clearInterval(interval);
          // Only works if the tab was opened via javascript, otherwise it
          // lacks permission to close itself.
          window.close();
          return 0;
        }
        return prev - 1;
      });
    }, 1_000);

    return () => clearInterval(interval);
  }, [shouldAutoClose]);

  if (isSuccessStatus) {
    return (
      <Layout>
        <CoeLogo />

        <div>
          <Typography variant="body1" fontWeight={600}>
            Your connection was successful!
          </Typography>

          <Typography>
            {shouldAutoClose
              ? `This page will automatically close in ${secondsLeft} second${
                  secondsLeft === 1 ? "" : "s"
                }.`
              : "You can close this page and return to your worksheet."}
          </Typography>
        </div>
      </Layout>
    );
  }

  const errorMessage = searchParams.get("error_message");
  const errorTitle = searchParams.get("error_title");

  return (
    <Layout>
      <CoeLogo />

      <div>
        <Typography variant="body1" fontWeight={600}>
          {errorTitle ?? "An error occurred while creating your connection."}
        </Typography>

        <Typography>{errorMessage ?? "Please close this page and try again."}</Typography>
      </div>
    </Layout>
  );
};
